import { useEffect, useState } from "react";
import {
  Table,
  Button,
  Drawer,
  Form,
  Input,
  Select,
  Switch,
  Tag,
  Popconfirm,
  Space,
  Typography,
  Avatar,
} from "antd";
import { OLIVEEB_COLORS } from "utils/colors";
import useWebsiteTemplates from "hooks/web-templates/useWebsiteTemplates";

const { TextArea } = Input;
const { Text } = Typography;

const TYPE_OPTIONS = [
  { value: "BUSINESS", label: "Business" },
  { value: "PORTFOLIO", label: "Portfolio" },
];

const CATEGORY_OPTIONS = [
  { value: "E-commerce", label: "E-commerce" },
  { value: "Landing page", label: "Landing page" },
  { value: "Courses & Coaching", label: "Courses & Coaching" },
  { value: "Education", label: "Education" },
  { value: "Marketing & Advertising", label: "Marketing & Advertising" },
  { value: "Construction & Interior", label: "Construction & Interior" },
  { value: "Sport & Lifestyle", label: "Sport & Lifestyle" },
  { value: "Business & IT", label: "Business & IT" },
  { value: "Homecare Services", label: "Homecare Services" },
  { value: "Restaurant & Food", label: "Restaurant & Food" },
  { value: "Portfolio & CV", label: "Portfolio & CV" },
  { value: "Medical & health", label: "Medical & health" },
  { value: "Fashion & Beauty", label: "Fashion & Beauty" },
  { value: "Nonprofit & Church", label: "Nonprofit & Church" },
  { value: "Home & Gardening", label: "Home & Gardening" },
  { value: "Legal & Finance", label: "Legal & Finance" },
  { value: "Travel & Tourism", label: "Travel & Tourism" },
  { value: "Art & Design", label: "Art & Design" },
  {
    value: "Agriculture & Manufacturing",
    label: "Agriculture & Manufacturing",
  },
  { value: "Pets & Animals", label: "Pets & Animals" },
  {
    value: "Automotive & transportation",
    label: "Automotive & transportation",
  },
  { value: "Real Estate", label: "Real Estate" },
  { value: "Wedding", label: "Wedding" },
  { value: "Events", label: "Events" },
  { value: "Entertainment & Music", label: "Entertainment & Music" },
];

// suggestions only — the field accepts free-form tags too
const SECTION_SUGGESTIONS = [
  "HERO",
  "ABOUT",
  "FEATURES",
  "SERVICES",
  "TESTIMONIALS",
  "CONTACT",
  "GALLERY",
  "FAQ",
  "TEAM",
  "PRICING",
].map((s) => ({ value: s, label: s }));

const C = {
  border: "rgba(0,0,0,0.10)",
  text: "#1a1a1a",
  textMuted: "#6b6b6b",
  bgSurface: "#f7f6f3",
};

const emptyForm = {
  slug: "",
  name: "",
  description: "",
  thumbnailUrl: "",
  category: "",
  type: "BUSINESS",
  tags: [],
  sections: [],
  active: true,
};

function useDebouncedValue(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

export default function TemplateAdminPage() {
  const {
    templates,
    loading,
    totalElements,
    query,
    updateQuery,
    createTemplate,
    updateTemplate,
    deactivateTemplate,
    reactivateTemplate,
  } = useWebsiteTemplates();

  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput, 300).trim();

  useEffect(() => {
    updateQuery({ search: debouncedSearch });
  }, [debouncedSearch, updateQuery]);

  const [saving, setSaving] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingSlug, setEditingSlug] = useState(null);
  const [form] = Form.useForm();

  const openCreate = () => {
    setEditingSlug(null);
    form.setFieldsValue(emptyForm);
    setDrawerOpen(true);
  };

  const openEdit = (record) => {
    setEditingSlug(record.slug);
    form.setFieldsValue({
      slug: record.slug,
      name: record.name,
      description: record.description,
      thumbnailUrl: record.thumbnailUrl,
      category: record.category,
      type: record.type,
      tags: record.tags || [],
      sections: record.sections || [],
      active: record.active,
    });
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editingSlug) {
        await updateTemplate(editingSlug, values);
      } else {
        await createTemplate(values);
      }
      closeDrawer();
    } catch (err) {
      if (err?.errorFields) return; // form validation error, already shown inline
      // any other failure was already toasted by the hook
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = (slug) => {
    deactivateTemplate(slug).catch(() => {});
  };

  const handleReactivate = (slug) => {
    reactivateTemplate(slug).catch(() => {});
  };

  const columns = [
    {
      title: "",
      dataIndex: "thumbnailUrl",
      width: 56,
      render: (url, record) => (
        <Avatar
          shape="square"
          size={40}
          src={url}
          style={{ background: C.bgSurface }}
        >
          {record.name?.[0]}
        </Avatar>
      ),
    },
    {
      title: "Template",
      dataIndex: "name",
      render: (name, record) => (
        <div>
          <div style={{ fontWeight: 600, color: C.text }}>{name}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.slug}
          </Text>
        </div>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      width: 110,
      render: (type) => (
        <Tag color={type === "PORTFOLIO" ? "purple" : "blue"}>
          {type === "PORTFOLIO" ? "Portfolio" : "Business"}
        </Tag>
      ),
    },
    {
      title: "Category",
      dataIndex: "category",
      width: 150,
    },
    {
      title: "Tags",
      dataIndex: "tags",
      render: (tags) => (
        <Space size={[4, 4]} wrap>
          {(tags || []).map((tag) => (
            <Tag key={tag} style={{ margin: 0 }}>
              {tag}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "active",
      width: 100,
      render: (active) =>
        active ? (
          <Tag color="green">Active</Tag>
        ) : (
          <Tag color="default">Inactive</Tag>
        ),
    },
    {
      title: "",
      key: "actions",
      width: 180,
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => openEdit(record)}>
            Edit
          </Button>
          {record.active ? (
            <Popconfirm
              title="Deactivate this template?"
              description="It will be hidden from the marketplace. Existing sites built with it are unaffected."
              okText="Deactivate"
              okButtonProps={{ danger: true }}
              onConfirm={() => handleDeactivate(record.slug)}
            >
              <Button size="small" danger>
                Deactivate
              </Button>
            </Popconfirm>
          ) : (
            <Button size="small" onClick={() => handleReactivate(record.slug)}>
              Reactivate
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <div>
          <h1
            style={{ fontSize: 18, fontWeight: 600, color: C.text, margin: 0 }}
          >
            Templates
          </h1>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Create, edit, and manage the templates shown in the marketplace
          </Text>
        </div>
        <Button
          type="primary"
          onClick={openCreate}
          style={{
            background: OLIVEEB_COLORS.oliveebGreen,
            borderColor: OLIVEEB_COLORS.oliveebGreen,
          }}
        >
          New Template
        </Button>
      </div>

      {/* search + filters */}
      <div
        style={{
          display: "flex",
          gap: 10,
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
        <Input.Search
          allowClear
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search name, slug, description, category or tag"
          style={{ flex: 1, minWidth: 240, maxWidth: 420 }}
        />
        <Select
          value={query.type ?? "all"}
          onChange={(v) => updateQuery({ type: v === "all" ? null : v })}
          style={{ width: 150 }}
          options={[{ value: "all", label: "All types" }, ...TYPE_OPTIONS]}
        />
        <Select
          value={
            query.active === null ? "all" : query.active ? "active" : "inactive"
          }
          onChange={(v) =>
            updateQuery({ active: v === "all" ? null : v === "active" })
          }
          style={{ width: 150 }}
          options={STATUS_OPTIONS}
        />
      </div>

      <Text
        type="secondary"
        style={{ fontSize: 12, display: "block", marginBottom: 8 }}
      >
        {totalElements} {totalElements === 1 ? "template" : "templates"}
        {debouncedSearch || query.type || query.active !== null
          ? " match your filters"
          : ""}
      </Text>

      <Table
        rowKey="slug"
        loading={loading}
        columns={columns}
        dataSource={templates}
        locale={{ emptyText: "No templates found" }}
        pagination={{
          current: query.page + 1,
          pageSize: query.size,
          total: totalElements,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50],
          hideOnSinglePage: totalElements <= 10,
        }}
        onChange={(pagination) => {
          if (pagination.pageSize !== query.size) {
            updateQuery({ size: pagination.pageSize }); // also resets to page 0
          } else {
            updateQuery({ page: pagination.current - 1 });
          }
        }}
        style={{
          border: `0.5px solid ${C.border}`,
          borderRadius: 10,
          overflow: "hidden",
        }}
      />

      <Drawer
        title={editingSlug ? `Edit "${editingSlug}"` : "New template"}
        open={drawerOpen}
        onClose={closeDrawer}
        size={600}
        zIndex={9999}
        extra={
          <Space>
            <Button onClick={closeDrawer}>Cancel</Button>
            <Button
              type="primary"
              loading={saving}
              onClick={handleSubmit}
              style={{
                background: OLIVEEB_COLORS.oliveebGreen,
                borderColor: OLIVEEB_COLORS.oliveebGreen,
              }}
            >
              {editingSlug ? "Save changes" : "Create template"}
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical" initialValues={emptyForm}>
          <Form.Item
            name="slug"
            label="Slug"
            rules={[
              { required: true, message: "Required" },
              {
                pattern: /^[a-z0-9]+(-[a-z0-9]+)*$/,
                message:
                  "Lowercase letters, numbers, and dashes only, e.g. 'start-up'",
              },
            ]}
            extra={editingSlug ? undefined : "Can't be changed after creation."}
          >
            <Input placeholder="e.g. start-up" disabled={!!editingSlug} />
          </Form.Item>

          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Required" }]}
          >
            <Input placeholder="e.g. Start Up" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea
              rows={3}
              placeholder="Short description shown on the card"
            />
          </Form.Item>

          <Form.Item name="thumbnailUrl" label="Thumbnail URL">
            <Input placeholder="https://..." />
          </Form.Item>

          <Form.Item name="category" label="Category">
            <Select
              allowClear
              placeholder="Search or select a category"
              options={CATEGORY_OPTIONS}
              showSearch={{
                filter: (input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase()),
              }}
            />
          </Form.Item>

          <Form.Item
            name="type"
            label="Type"
            rules={[{ required: true, message: "Required" }]}
          >
            <Select options={TYPE_OPTIONS} />
          </Form.Item>

          <Form.Item name="tags" label="Tags">
            <Select
              mode="tags"
              placeholder="Add tags and press enter"
              tokenSeparators={[","]}
            />
          </Form.Item>

          <Form.Item name="sections" label="Sections">
            <Select
              mode="tags"
              placeholder="Add sections and press enter"
              options={SECTION_SUGGESTIONS}
              tokenSeparators={[","]}
            />
          </Form.Item>

          <Form.Item name="active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
}
